import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard from "@/components/feature/ComicCard";
import BackToTop from "@/components/feature/BackToTop";
import { categories, comics } from "@/mocks/comics";

const sortOptions = [
  { key: "update", label: "最近更新" },
  { key: "rating", label: "评分最高" },
  { key: "chapters", label: "话数最多" },
];

export default function CategoryPage() {
  const { slug = "fantasy" } = useParams();
  const [sortKey, setSortKey] = useState("update");
  const [search, setSearch] = useState("");

  const category = categories.find((c) => c.slug === slug) ?? categories[0];
  const categoryList = useMemo(
    () => comics.filter((c) => c.categorySlug === slug),
    [slug],
  );

  const sorted = useMemo(() => {
    let list = [...categoryList];
    if (search.trim()) {
      const kw = search.trim().toLowerCase();
      list = list.filter(
        (c) =>
          c.title.toLowerCase().includes(kw) ||
          c.tags.some((t) => t.toLowerCase().includes(kw)) ||
          c.description.includes(kw),
      );
    }
    if (sortKey === "rating") list.sort((a, b) => b.rating - a.rating);
    if (sortKey === "chapters") list.sort((a, b) => b.chapters - a.chapters);
    if (sortKey === "update") list.sort((a, b) => b.rating - a.rating);
    return list;
  }, [categoryList, sortKey, search]);

  return (
    <div className="min-h-screen bg-background-50 text-foreground-950">
      <Navbar />

      {/* 面包屑 */}
      <div className="mx-auto max-w-7xl px-4 md:px-6 pt-24 md:pt-28">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-sm text-foreground-600">
          <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line text-foreground-400"></i>
          <span className="text-foreground-950">{category.name}</span>
        </nav>
      </div>

      {/* 分类头图 */}
      <section className="relative mx-auto mt-6 max-w-7xl overflow-hidden rounded-lg px-4 md:px-6">
        <div className="relative overflow-hidden rounded-lg">
          <img
            src={category.cover}
            alt={`${category.name} 妖精漫画分类封面`}
            title={`${category.name} 韩国妖精漫画`}
            className="h-56 w-full object-cover md:h-72"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-background-50/20"></div>
          <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
            <p className="flex items-center gap-2 text-sm font-medium text-primary-400">
              <i className={`${category.icon}`}></i>
              {category.en} · {category.count} 部作品
            </p>
            <h1 className="mt-2 font-heading text-2xl font-black md:text-4xl">{category.name}漫画</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-foreground-700 md:text-base">{category.desc}</p>
          </div>
        </div>
      </section>

      {/* 分类切换 */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 mt-8">
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm transition-colors ${
                c.slug === slug
                  ? "bg-primary-500 text-background-50"
                  : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
              }`}
            >
              <i className={`${c.icon} text-sm`}></i>
              {c.name}
            </Link>
          ))}
        </div>
      </section>

      {/* 筛选与列表 */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 mt-8 pb-16">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {sortOptions.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setSortKey(opt.key)}
                className={`rounded-md px-4 py-2 text-sm whitespace-nowrap transition-colors ${
                  sortKey === opt.key
                    ? "bg-secondary-100 text-secondary-900 ring-1 ring-secondary-300"
                    : "text-foreground-600 hover:bg-background-100"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-64">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜索本分类作品"
              className="w-full rounded-md border border-background-200 bg-background-100 py-2 pl-9 pr-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-300 focus:outline-none"
            />
          </div>
        </div>

        {sorted.length > 0 ? (
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {sorted.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center py-16 text-center">
            <i className="ri-inbox-2-line text-5xl text-foreground-300"></i>
            <p className="mt-4 text-sm text-foreground-600">没有找到匹配「{search}」的作品</p>
            <button
              type="button"
              onClick={() => setSearch("")}
              className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2 text-sm text-background-50 hover:bg-primary-600"
            >
              清除搜索
            </button>
          </div>
        )}
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
}